import { useGetIdentity, useGetList } from "ra-core";
import { Link } from "react-router";
import {
  differenceInCalendarDays,
  differenceInHours,
  differenceInMinutes,
  format,
} from "date-fns";
import { fr } from "date-fns/locale";
import { UserPlus } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { getContactDisplayName } from "../contacts/contactModel";
import type { Contact } from "../types";

// Sprint 9.2 + 10.x — NewSignups : contacts récemment ajoutés (≤ 7j).
// Groupage "Aujourd'hui" / "Hier" / "Cette semaine". Source : contacts_summary.first_seen.

const PASTEL = [
  "bg-fabrik-orange-soft text-fabrik-orange",
  "bg-fabrik-blue-soft text-fabrik-blue",
  "bg-fabrik-green-soft text-fabrik-accent-text",
  "bg-fabrik-purple-soft text-fabrik-purple",
  "bg-fabrik-red-soft text-fabrik-red",
] as const;

function initials(first?: string, last?: string) {
  return `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase() || "?";
}

function avatarColor(id: number | string) {
  const n = typeof id === "number" ? id : String(id).length;
  return PASTEL[n % PASTEL.length];
}

function formatSignupDate(
  firstSeen: string,
  group: "today" | "yesterday" | "week",
): string {
  const date = new Date(firstSeen);
  const now = new Date();
  if (group === "today") {
    const mins = differenceInMinutes(now, date);
    if (mins < 60) return `il y a ${Math.max(1, mins)} min`;
    const hrs = differenceInHours(now, date);
    return `il y a ${hrs} h`;
  }
  if (group === "yesterday") {
    return `hier à ${format(date, "HH:mm", { locale: fr })}`;
  }
  return format(date, "EEEE dd/MM HH:mm", { locale: fr });
}

function SignupRow({
  contact,
  group,
}: {
  contact: Contact;
  group: "today" | "yesterday" | "week";
}) {
  return (
    <Link
      to={`/contacts/${contact.id}/show`}
      className="flex items-center gap-3 py-1.5 hover:bg-fabrik-green-soft/50 rounded px-1 -mx-1"
    >
      <div
        className={`size-7 rounded-full flex items-center justify-center text-[11px] font-semibold ${avatarColor(contact.id)}`}
      >
        {initials(contact.first_name, contact.last_name)}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">
          {getContactDisplayName(contact)}
        </p>
        {contact.company_name && (
          <p className="text-xs text-muted-foreground truncate">
            {contact.company_name}
          </p>
        )}
      </div>
      <span className="text-[11px] text-fabrik-text-3 shrink-0">
        {formatSignupDate(contact.first_seen, group)}
      </span>
    </Link>
  );
}

export const NewSignups = () => {
  const { identity } = useGetIdentity();
  const { data, isPending } = useGetList<Contact>(
    "contacts_summary",
    {
      pagination: { page: 1, perPage: 30 },
      sort: { field: "first_seen", order: "DESC" },
      filter: { "archived_at@is": null },
    },
    { enabled: !!identity?.id },
  );

  const today = new Date();
  const aujourdhui: Contact[] = [];
  const hier: Contact[] = [];
  const semaine: Contact[] = [];
  (data ?? []).forEach((c) => {
    if (!c.first_seen) return;
    const diff = differenceInCalendarDays(today, new Date(c.first_seen));
    if (diff === 0) aujourdhui.push(c);
    else if (diff === 1) hier.push(c);
    else if (diff > 1 && diff <= 7) semaine.push(c);
  });

  const total = aujourdhui.length + hier.length + semaine.length;

  return (
    <Card>
      <CardContent className="py-4 md:py-5">
        <div className="flex items-center gap-2 mb-3">
          <UserPlus className="size-4 text-fabrik-accent-text" />
          <p className="text-[13px] font-semibold text-foreground">
            Nouveaux inscrits
          </p>
          {total > 0 && (
            <span className="text-[11px] text-muted-foreground">({total})</span>
          )}
        </div>
        {isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : total === 0 ? (
          <p className="text-xs text-muted-foreground py-2">
            Aucun nouvel inscrit cette semaine.
          </p>
        ) : (
          <>
            {aujourdhui.length > 0 && (
              <>
                <div className="text-[10.5px] uppercase tracking-wider text-fabrik-text-3 mt-1 mb-1">
                  Aujourd'hui
                </div>
                {aujourdhui.slice(0, 5).map((c) => (
                  <SignupRow key={c.id} contact={c} group="today" />
                ))}
              </>
            )}
            {hier.length > 0 && (
              <>
                <div className="text-[10.5px] uppercase tracking-wider text-fabrik-text-3 mt-2 mb-1">
                  Hier
                </div>
                {hier.slice(0, 5).map((c) => (
                  <SignupRow key={c.id} contact={c} group="yesterday" />
                ))}
              </>
            )}
            {semaine.length > 0 && (
              <>
                <div className="text-[10.5px] uppercase tracking-wider text-fabrik-text-3 mt-2 mb-1">
                  Cette semaine
                </div>
                {semaine.slice(0, 5).map((c) => (
                  <SignupRow key={c.id} contact={c} group="week" />
                ))}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
